import { apiClient } from './api';
import {
  ApiFlightTrackCollection,
  FlightTrack,
  toFlightTrack,
} from '../types/flightTrack';

/**
 * How much flying to ask for, in minutes.
 *
 * The endpoint defaults to the last hour and caps at 1440, so this asks for the
 * whole day it is willing to give: "where has this drone been" is a question
 * about today, and an hour-long window shows an empty map for a flight that
 * finished at lunchtime.
 */
const TRACK_WINDOW_MINUTES = 1440;

export const telemetryService = {
  /**
   * The path a drone has flown in the last day, or `null` when it has not
   * flown.
   *
   * `null` is the ordinary answer, not a failure: the server replies `200` with
   * `features: []` for a drone with no fixes in the window, which today is most
   * of them. {@link toFlightTrack} turns that into `null` rather than an empty
   * polyline so the view can say so.
   *
   * A `404` still throws. It means the drone is not there *or is not this
   * pilot's* — the backend answers 404 rather than 403 for another operator's
   * aircraft, deliberately, so the fleet cannot be mapped by probing the
   * difference (#382). Collapsing it to `null` here would render "this drone
   * has not flown" over somebody else's aircraft, which is a worse answer than
   * an honest refusal.
   */
  async getFlightTrack(
    droneId: string,
    minutes: number = TRACK_WINDOW_MINUTES
  ): Promise<FlightTrack | null> {
    const collection = await apiClient.get<ApiFlightTrackCollection>(
      `/api/v1/drones/${encodeURIComponent(droneId)}/telemetry/track?minutes=${minutes}`
    );
    return toFlightTrack(collection);
  },
};
